import React from 'react';
import { Link } from 'react-router-dom';
import { Moon, Mail, Shield, FileText, DollarSign, RefreshCw, Book, HelpCircle, Settings, Crown } from 'lucide-react';
import { useUserStore } from '../../store';

export const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { isProUser, setProfile, setSubscription, clearProfile } = useUserStore();

  const activateProUser = () => {
    setProfile({
      id: 'footer-pro-user',
      name: 'Pro Demo User',
      email: 'pro@demo.com'
    });
    
    setSubscription({
      plan: 'pro',
      status: 'active',
      startDate: new Date().toISOString(),
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      autoRenew: true
    });
  };

  const legalLinks = [
    { path: '/terms', label: 'Terms of Service', icon: <FileText className="h-4 w-4" /> },
    { path: '/privacy', label: 'Privacy Policy', icon: <Shield className="h-4 w-4" /> },
    { path: '/refunds', label: 'Refund Policy', icon: <RefreshCw className="h-4 w-4" /> }
  ];

  return (
    <footer className="bg-slate-900 text-slate-300 border-t border-slate-800">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-white">
                <Moon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">SleepCalc Pro</h3>
                <p className="text-sm text-slate-400">Professional Sleep Optimization</p>
              </div>
            </div>
            <p className="text-slate-400 mb-4 max-w-md">
              Transform your sleep quality with AI-powered recommendations, advanced analytics, and personalized sleep optimization strategies.
            </p>
            <p className="text-sm text-slate-500">
              Version 3.0 - Built with care for better sleep
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/" 
                  className="text-slate-400 hover:text-blue-400 transition-colors text-sm"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link 
                  to="/how-it-works" 
                  className="text-slate-400 hover:text-blue-400 transition-colors text-sm flex items-center space-x-1"
                >
                  <HelpCircle className="h-3 w-3" />
                  <span>How It Works</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/sleep-guide" 
                  className="text-slate-400 hover:text-blue-400 transition-colors text-sm flex items-center space-x-1"
                >
                  <Book className="h-3 w-3" />
                  <span>Sleep Guide</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/faq" 
                  className="text-slate-400 hover:text-blue-400 transition-colors text-sm flex items-center space-x-1"
                >
                  <HelpCircle className="h-3 w-3" />
                  <span>FAQ</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/pricing" 
                  className="text-slate-400 hover:text-blue-400 transition-colors text-sm flex items-center space-x-1"
                >
                  <DollarSign className="h-3 w-3" />
                  <span>Pricing</span>
                </Link>
              </li>
              <li>
                <a 
                  href="mailto:support@sleepcalc.pro" 
                  className="text-slate-400 hover:text-blue-400 transition-colors text-sm flex items-center space-x-1"
                >
                  <Mail className="h-3 w-3" />
                  <span>Support</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-2">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-slate-400 hover:text-blue-400 transition-colors text-sm flex items-center space-x-1"
                  >
                    {link.icon}
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-slate-500">
              © {currentYear} SleepCalc Pro. All rights reserved.
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-slate-500">
                Made with ❤️ for better sleep
              </div>
              {/* Pro Activation Button */}
              <button
                onClick={isProUser ? clearProfile : activateProUser}
                className="text-xs px-3 py-1 rounded-full border border-slate-600 hover:border-blue-500 text-slate-400 hover:text-blue-400 transition-colors flex items-center space-x-1"
                title={isProUser ? "Switch to Free" : "Activate Pro Demo"}
              >
                {isProUser ? (
                  <>
                    <Settings className="h-3 w-3" />
                    <span>Demo: Free</span>
                  </>
                ) : (
                  <>
                    <Crown className="h-3 w-3" />
                    <span>Demo: Pro</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};